import { dependencies } from '@pn/core/dependencies';
import type { SearchResult } from '@pn/core/domain/search';
import { useNavigation } from '@pn/core/operations/dataSelection';
import { notificationService } from '@pn/services/notifications';
import { getBBox } from '@pn/services/utils/geojson';
import { isNil } from 'lodash-es';
import React from 'react';

export function useHandleSearchResult() {
  const { navigateTo } = useNavigation();

  return React.useCallback(
    async (searchResult: SearchResult): Promise<void> => {
      const { map } = dependencies;
      const { info } = notificationService;

      clearGridSearchResultLayer();

      if (searchResult.resultType === 'location') {
        if (isNil(searchResult.geometry)) {
          info('No location found');
          return;
        }

        addGridSearchResultLayer(searchResult.geometry);

        const bbox = !isNil(searchResult.bbox)
          ? searchResult.bbox
          : getBBox(searchResult.geometry);

        map.fitToBBox(bbox, {
          padding: '20%',
          maxZoom: 13,
        });
      } else {
        navigateTo({
          dataType: searchResult.dataType,
          id: searchResult._id,
          state: { autoZoom: true },
        });

        if (isNil(searchResult.geometry)) info('No location found');
      }
    },
    [navigateTo]
  );
}

// TODO: move map-related functions into the map service
function addGridSearchResultLayer(geometry: GeoJSON.Geometry): void {
  const mapboxMap = dependencies.map._native;

  mapboxMap.addSource('grid-search-result', {
    type: 'geojson',
    data: geometry,
  });

  if (geometry.type === 'Point') {
    mapboxMap.addLayer({
      id: 'grid-search-result-layer',
      type: 'symbol',
      source: 'grid-search-result',
      layout: {
        'icon-ignore-placement': true,
        'icon-image': 'custom-marker',
        'icon-size': 1.33,
        'icon-offset': [0, -15],
      },
      paint: {
        'icon-color': '#E91E63',
      },
    });
  } else {
    mapboxMap.addLayer({
      id: 'grid-search-result-layer',
      type: 'line',
      source: 'grid-search-result',
      paint: {
        'line-color': '#E91E63',
        'line-width': 4,
      },
    });
  }
}

export function clearGridSearchResultLayer() {
  const mapboxMap = dependencies.map._native;

  if (mapboxMap.getLayer('grid-search-result-layer')) {
    mapboxMap.removeLayer('grid-search-result-layer');
  }
  if (mapboxMap.getSource('grid-search-result')) {
    mapboxMap.removeSource('grid-search-result');
  }
}
